import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import PromoBanner from '../../components/layout/PromoBanner';
import WalletGauge from '../../components/wallet/WalletGauge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Wallet, 
  FolderKanban, 
  MessageSquare,
  CheckCircle2,
  Plus,
  ArrowUpRight,
  CreditCard,
  Clock,
  Instagram,
  Facebook,
  Linkedin,
  TrendingUp,
  Users,
  Eye
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useSearchParams, useNavigate } from 'react-router-dom';

const ClientDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [projects, setProjects] = useState([]);
  const [socialStats, setSocialStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme();

  useEffect(() => {
    fetchData();
    
    // Check for payment success
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [dashboardRes, walletRes, projectsRes] = await Promise.all([
        api.get('/dashboard/client'),
        api.get('/wallet/'),
        api.get('/projects/')
      ]);
      setDashboard(dashboardRes.data);
      setWallet(walletRes.data);
      setProjects(projectsRes.data);
      
      // Mock social stats (in production, fetch from API)
      setSocialStats({
        instagram: { followers: 2450, growth: 12.5, engagement: 4.8 },
        facebook: { followers: 1890, growth: 5.2, engagement: 2.1 },
        linkedin: { followers: 890, growth: 8.7, engagement: 3.5 },
        lastUpdated: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Paiement réussi ! Vos heures ont été ajoutées.');
        fetchData();
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      brief_pending: 'bg-yellow-100 text-yellow-700',
      brief_signed: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-purple-100 text-purple-700',
      review: 'bg-orange-100 text-orange-700',
      delivered: 'bg-green-100 text-green-700',
      completed: 'bg-emerald-100 text-emerald-700',
    };
    const labels = {
      draft: 'Brouillon',
      brief_pending: 'Brief en attente',
      brief_signed: 'Brief validé',
      in_progress: 'En cours',
      review: 'En révision',
      delivered: 'Livré',
      completed: 'Terminé',
    };
    return (
      <Badge className={cn('font-medium', styles[status] || styles.draft)}>
        {labels[status] || status}
      </Badge>
    );
  };

  const projectTypeIcons = {
    logo: '🎨',
    visual: '🖼️',
    video: '🎬',
    website: '🌐',
    mobile_app: '📱',
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      {/* Promo Banner - visible uniquement pour les clients - positionné après le header */}
      <div className={cn(
        "fixed top-16 right-0 z-40 transition-all duration-300",
        collapsed ? "left-20" : "left-64"
      )}>
        <PromoBanner forceShow={true} />
      </div>
      
      <main className={cn(
        "transition-all duration-300 p-6 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#1a365d]" data-testid="dashboard-title">
            Bienvenue, {user?.first_name} 👋
          </h1>
          <p className="text-[#64748b] mt-1">
            {user?.company_name && `${user.company_name} • `}Espace client
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : (
          <>
            {/* Top Section - Wallet & Quick Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
              {/* Wallet Card */}
              <Card className="card lg:col-span-1" data-testid="wallet-card">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-[#1a365d] flex items-center gap-2">
                    <Wallet className="w-5 h-5" />
                    Mon Wallet
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col items-center">
                  <WalletGauge 
                    balance={wallet?.balance_hours || 0}
                    maxBalance={100}
                    hourlyRate={wallet?.hourly_rate || 75}
                  />
                  <Button 
                    onClick={() => navigate('/wallet')}
                    className="btn-primary mt-6 w-full"
                    data-testid="topup-wallet-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Recharger
                  </Button>
                </CardContent>
              </Card>

              {/* Stats */}
              <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Projets actifs */}
                <Card 
                  className="card card-hover cursor-pointer" 
                  data-testid="stat-projects"
                  onClick={() => navigate('/my-projects')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-[#6366f1]/10 rounded-xl flex items-center justify-center">
                          <FolderKanban className="w-5 h-5 text-[#6366f1]" />
                        </div>
                        <div>
                          <p className="text-xs text-[#64748b]">Projets actifs</p>
                          <p className="font-mono text-xl font-bold text-[#1a365d]">
                            {dashboard?.active_projects || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Preview list */}
                    <div className="space-y-2 border-t border-[#e2e8f0] pt-3">
                      {dashboard?.active_projects_list?.length > 0 ? (
                        <>
                          {dashboard.active_projects_list.map((project, idx) => (
                            <div key={project.id || idx} className="flex items-center gap-2 text-xs">
                              <span className="text-base">{projectTypeIcons[project.project_type] || '📁'}</span>
                              <span className="truncate flex-1 text-[#334155]">{project.name}</span>
                              {getStatusBadge(project.status)}
                            </div>
                          ))}
                          {dashboard.active_projects > 3 && (
                            <p className="text-xs text-[#6366f1] font-medium text-center pt-1">
                              +{dashboard.active_projects - 3} autre{dashboard.active_projects - 3 > 1 ? 's' : ''}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-[#94a3b8] italic">Aucun projet actif</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* À valider */}
                <Card 
                  className="card card-hover cursor-pointer" 
                  data-testid="stat-validations"
                  onClick={() => navigate('/my-projects?filter=review')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-[#f59e0b]/10 rounded-xl flex items-center justify-center">
                          <CheckCircle2 className="w-5 h-5 text-[#f59e0b]" />
                        </div>
                        <div>
                          <p className="text-xs text-[#64748b]">À valider</p>
                          <p className="font-mono text-xl font-bold text-[#f59e0b]">
                            {dashboard?.pending_validations || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Preview list */}
                    <div className="space-y-2 border-t border-[#e2e8f0] pt-3">
                      {dashboard?.pending_validations_list?.length > 0 ? (
                        dashboard.pending_validations_list.map((project, idx) => (
                          <div key={project.id || idx} className="flex items-center gap-2 text-xs">
                            <span className="text-base">{projectTypeIcons[project.project_type] || '📁'}</span>
                            <span className="truncate flex-1 text-[#334155]">{project.name}</span>
                            <Badge className="bg-orange-100 text-orange-700 text-[10px]">BAT</Badge>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-[#94a3b8] italic">Rien à valider</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Tickets ouverts */}
                <Card 
                  className="card card-hover cursor-pointer" 
                  data-testid="stat-tickets"
                  onClick={() => navigate('/tickets')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-[#00d4aa]/10 rounded-xl flex items-center justify-center">
                          <MessageSquare className="w-5 h-5 text-[#00d4aa]" />
                        </div>
                        <div>
                          <p className="text-xs text-[#64748b]">Tickets ouverts</p>
                          <p className="font-mono text-xl font-bold text-[#1a365d]">
                            {dashboard?.open_tickets || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Preview list */}
                    <div className="space-y-2 border-t border-[#e2e8f0] pt-3">
                      {dashboard?.open_tickets_list?.length > 0 ? (
                        <>
                          {dashboard.open_tickets_list.map((ticket, idx) => (
                            <div key={ticket.id || idx} className="flex items-center gap-2 text-xs">
                              <MessageSquare className="w-3 h-3 text-[#64748b]" />
                              <span className="truncate flex-1 text-[#334155]">{ticket.subject}</span>
                              <Badge className={cn(
                                "text-[10px]",
                                ticket.priority === 'urgent' ? "bg-red-100 text-red-700" :
                                ticket.priority === 'high' ? "bg-orange-100 text-orange-700" :
                                "bg-blue-100 text-blue-700"
                              )}>
                                {ticket.priority === 'urgent' ? 'Urgent' : 
                                 ticket.priority === 'high' ? 'Haute' : 'Normal'}
                              </Badge>
                            </div>
                          ))}
                          {dashboard.open_tickets > 3 && (
                            <p className="text-xs text-[#00d4aa] font-medium text-center pt-1">
                              +{dashboard.open_tickets - 3} autre{dashboard.open_tickets - 3 > 1 ? 's' : ''}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-[#94a3b8] italic">Aucun ticket ouvert</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Projects List */}
            <Card className="card" data-testid="projects-list">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold text-[#1a365d]">
                  Mes projets
                </CardTitle>
                <Button 
                  onClick={() => navigate('/my-projects')}
                  variant="ghost" 
                  size="sm" 
                  className="text-[#00d4aa]"
                >
                  Voir tout <ArrowUpRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {projects.slice(0, 5).map((project) => (
                    <div 
                      key={project.id}
                      className="flex items-center justify-between p-4 rounded-xl border border-[#e2e8f0] hover:border-[#00d4aa]/50 transition-all cursor-pointer group"
                      onClick={() => navigate(`/projects/${project.id}`)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#f8fafc] rounded-xl flex items-center justify-center text-2xl">
                          {projectTypeIcons[project.project_type] || '📁'}
                        </div>
                        <div>
                          <h4 className="font-medium text-[#1a365d] group-hover:text-[#00d4aa] transition-colors">
                            {project.name}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            {getStatusBadge(project.status)}
                            <span className="text-xs text-[#64748b]">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {project.actual_hours?.toFixed(1) || 0}h / {project.estimated_hours}h
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="font-mono font-bold text-[#1a365d]">
                          {(project.actual_hours || 0).toFixed(1)}h
                        </p>
                        <p className="text-xs text-[#64748b]">
                          Budget: {project.estimated_hours || 0}h
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  {projects.length === 0 && (
                    <div className="text-center py-12 text-[#64748b]">
                      <FolderKanban className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="mb-4">Aucun projet pour le moment</p>
                      <Button className="btn-primary">
                        <Plus className="w-4 h-4 mr-2" />
                        Demander un devis
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Social Media Stats */}
            {socialStats && (
              <Card className="card mt-6" data-testid="social-stats">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-[#1a365d] flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#00d4aa]" />
                    Réseaux sociaux
                  </CardTitle>
                  <span className="text-xs text-[#64748b]">
                    Mis à jour {new Date(socialStats.lastUpdated).toLocaleDateString('fr-FR')}
                  </span>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Instagram */}
                    <div className="p-4 rounded-xl bg-gradient-to-br from-pink-50 to-purple-50 border border-pink-100">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                          <Instagram className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-medium text-[#334155]">Instagram</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Abonnés</span>
                          <span className="font-mono font-bold text-[#1a365d]">
                            {socialStats.instagram.followers.toLocaleString('fr-FR')}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Croissance</span>
                          <span className="text-sm text-emerald-600 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            +{socialStats.instagram.growth}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Engagement</span>
                          <span className="font-medium text-[#1a365d]">{socialStats.instagram.engagement}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Facebook */}
                    <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-[#1877F2] flex items-center justify-center">
                          <Facebook className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-medium text-[#334155]">Facebook</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Abonnés</span>
                          <span className="font-mono font-bold text-[#1a365d]">
                            {socialStats.facebook.followers.toLocaleString('fr-FR')}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Croissance</span>
                          <span className="text-sm text-emerald-600 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            +{socialStats.facebook.growth}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Engagement</span>
                          <span className="font-medium text-[#1a365d]">{socialStats.facebook.engagement}%</span>
                        </div>
                      </div>
                    </div>

                    {/* LinkedIn */}
                    <div className="p-4 rounded-xl bg-gradient-to-br from-sky-50 to-cyan-50 border border-sky-100">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-[#0A66C2] flex items-center justify-center">
                          <Linkedin className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-medium text-[#334155]">LinkedIn</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Abonnés</span>
                          <span className="font-mono font-bold text-[#1a365d]">
                            {socialStats.linkedin.followers.toLocaleString('fr-FR')}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Croissance</span>
                          <span className="text-sm text-emerald-600 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            +{socialStats.linkedin.growth}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Engagement</span>
                          <span className="font-medium text-[#1a365d]">{socialStats.linkedin.engagement}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <Button 
                variant="outline" 
                className="h-auto p-6 flex flex-col items-center gap-3 card card-hover"
                onClick={() => navigate('/my-projects/new')}
              >
                <Plus className="w-8 h-8 text-[#00d4aa]" />
                <span className="font-semibold text-[#1a365d]">Nouveau projet</span>
              </Button>
              
              <Button 
                variant="outline" 
                className="h-auto p-6 flex flex-col items-center gap-3 card card-hover"
                onClick={() => navigate('/tickets/new')}
              >
                <MessageSquare className="w-8 h-8 text-[#6366f1]" />
                <span className="font-semibold text-[#1a365d]">Ouvrir un ticket</span>
              </Button>
              
              <Button 
                variant="outline" 
                className="h-auto p-6 flex flex-col items-center gap-3 card card-hover"
                onClick={() => navigate('/wallet')}
              >
                <CreditCard className="w-8 h-8 text-[#f59e0b]" />
                <span className="font-semibold text-[#1a365d]">Recharger mon wallet</span>
              </Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default ClientDashboard;
